import axios from 'axios';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo
} from 'react';
import propTypes, { object } from 'prop-types';
import { useSelector } from 'react-redux';
import { ChevronLeft, ClipboardCheck, Table2 } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import AgentsTable from './AgentsTable';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useNotifyTourPageReady from 'tailwind-components/PortalWalkthrough/useNotifyTourPageReady';
import useTabUrlState from 'hooks/useTabUrlState';
import LoadingContent from 'components/LoadingContent/LoadingContent';
import { cn } from 'lib/utils';
import Separator from 'tailwind-components/Separator/Separator';
import { getCompany } from 'helpers/getCompanyVariables';
import { safePostCall, safeAWSCallGet } from 'helpers/firebaseSetup';
import { parseZohoJsonOutput } from 'helpers/zoho/response-parsers';
import { consolidateAgenciesByHierarchy } from 'helpers/agencyManagement/agency-tree';
import {
  AGENT_FILTER_ALL,
  DEFAULT_SORT_OPTION,
  filterAndSortAgents
} from 'helpers/agencyManagement/agent-filters';
import downloadCsv from 'helpers/csv';
import { isMobileWebView } from 'helpers/mobileWebView';
import endpoints from 'constants/endpoints';
import { ONEILL_MAIN_AGENCY_ID } from 'constants/enum';
import AgentRankTabs, {
  AGENT_TAB_IDS,
  filterAgentsByTab,
  getAgentTabCounts
} from './AgentRankTabs';
import OpenOffboardingsTable, {
  countOpenOffboardingAgents
} from './OpenOffboardingsTable';
import { getAgentStatusLabel, getOpenOffboardingRecord } from './agentStatus';
import { getAgentFlags } from './agentFlags';
import AgentsFiltersToolbar from 'tailwind-components/AgentsFiltersToolbar/AgentsFiltersToolbar';
import AgencyDashboardSectionTabs, {
  DASHBOARD_SECTION_IDS
} from './AgencyDashboardSectionTabs';
import AgencyOnboardingDashboard from './AgencyOnboardingDashboard';
import useAppliedAgencySelection from 'hooks/useAppliedAgencySelection';
import AgencyMultiSelectBar from '../AgencyMultiSelectBar';

const isOneill = getCompany() === 'oneill';

async function fetchOneAgencyOffboardings(agency) {
  try {
    const res = await safePostCall(endpoints.awsProxy.safePost, {
      agencyId: String(agency.id),
      endpoint: endpoints.zoho.getAgentsWithOpenOffboarding,
      isZohoRequest: true
    });
    const { ok, data } = parseZohoJsonOutput(res?.data?.details?.output);
    if (ok && data?.agencies) return data.agencies;
  } catch (e) {
    console.error(`[offboarding] request failed for agency ${agency.id}:`, e);
  }
  // Synthesized failure entry so the table can report which agency failed.
  return { [agency.id]: { ok: false, name: agency.name ?? '', agents: {} } };
}

const AGENT_EXPORT_COLUMNS = [
  { key: 'agentName', label: 'Agent Name' },
  { key: 'email', label: 'Email' },
  { key: 'npn', label: 'NPN' },
  { key: 'position', label: 'Position' },
  { key: 'status', label: 'Status' },
  { key: 'agency', label: 'Agency' },
  { key: 'flags', label: 'Flags' }
];

const DASHBOARD_SECTION_TABS = [
  {
    id: DASHBOARD_SECTION_IDS.AGENTS_TABLE,
    label: 'Agents Table',
    icon: <Table2 />
  },
  {
    id: DASHBOARD_SECTION_IDS.ONBOARDING,
    label: 'Onboarding Dashboard',
    icon: <ClipboardCheck />
  }
];

const AgencyDashboard = ({
  agencyData,
  agencyDashboardAgents,
  agenciesTree,
  setAgenciesTree,
  setLocalLoader,
  localLoader,
  getSpecificAgencyData,
  refreshUser,
  getSelectedAgenciesAgentsAndAppointmentCount
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  useNotifyTourPageReady(localLoader);
  const user = useSelector(state => state.user.zohoData);
  const rootAgencyId = user?.Account_Name?.id;
  const isOneillRootAgency =
    String(rootAgencyId ?? '') === ONEILL_MAIN_AGENCY_ID;

  const [dashboardSectionTab, setDashboardSectionTab] = useTabUrlState({
    param: 'section',
    tabIds: Object.values(DASHBOARD_SECTION_IDS),
    defaultTabId: DASHBOARD_SECTION_IDS.AGENTS_TABLE,
    correctInvalidToDefault: true
  });
  // rank tabs only live inside the agents-table section → drop ?rank in other sections
  const [agentTabId, setAgentTabId] = useTabUrlState({
    param: 'rank',
    tabIds: Object.values(AGENT_TAB_IDS),
    defaultTabId: AGENT_TAB_IDS.ACTIVE,
    active: dashboardSectionTab === DASHBOARD_SECTION_IDS.AGENTS_TABLE,
    correctInvalidToDefault: true
  });
  const [openOffboardings, setOpenOffboardings] = useState({
    loading: false,
    agencies: {}
  });
  const offboardingFetchGen = useRef(0);
  const lastOffboardingQueryRef = useRef([]);
  const agenciesTreeCacheRef = useRef(null);
  const [agentSearchQuery, setAgentSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState(AGENT_FILTER_ALL);
  const [sortOption, setSortOption] = useState(DEFAULT_SORT_OPTION);
  const [multiselectSelectedOptions, setmultiselectSelectedOptions] = useState(
    []
  );
  const { isDirty, apply, markApplied } = useAppliedAgencySelection(multiselectSelectedOptions);
  const [onboardingLoad, setOnboardingLoad] = useState({
    nonce: 0,
    agencies: []
  });
  const bumpOnboardingLoad = useCallback(requests => {
    setOnboardingLoad(prev => ({
      nonce: prev.nonce + 1,
      agencies: requests.map(r => ({
        agencyId: String(r.agencyId ?? r.id ?? r.value ?? ''),
        agencyName: String(r.agencyName ?? r.name ?? '')
      }))
    }));
  }, []);

  const multiselectAvailableOptions = useMemo(() => {
    if (!agencyData?.agencyInformation?.Downline_Agencies_Three) {
      return [];
    }
    return [
      agencyData.agencyInformation,
      ...agencyData.agencyInformation.Downline_Agencies_Three
    ].map(a => ({
      name: a?.name?._value,
      value: a?.id?._value
    }));
  }, [agencyData]);

  useEffect(() => {
    if (!agencyData?.agencyInformation?.Downline_Agencies_Three) {
      return;
    }
    const initial = [
      {
        name: agencyData.agencyInformation.name._value,
        value: agencyData.agencyInformation.id._value
      }
    ];
    setmultiselectSelectedOptions(initial);
    markApplied(initial);
  }, [agencyData]);

  // reset tabs/filters only when switching to a DIFFERENT agency — comparing the id (rather
  // than a mount flag, which StrictMode's double-invoke would trip) so a shared/reloaded URL
  // keeps its ?section / ?rank instead of snapping back to defaults on first load
  const prevAgencyIdRef = useRef(id);
  useEffect(() => {
    if (prevAgencyIdRef.current === id) return;
    prevAgencyIdRef.current = id;
    setAgentTabId(AGENT_TAB_IDS.ACTIVE);
    setAgentSearchQuery('');
    setPositionFilter(AGENT_FILTER_ALL);
    setSortOption(DEFAULT_SORT_OPTION);
    setDashboardSectionTab(DASHBOARD_SECTION_IDS.AGENTS_TABLE);
    setOnboardingLoad({ nonce: 0, agencies: [] });
  }, [id]);

  const fetchOpenOffboardings = useCallback(async queryAgencies => {
    const gen = ++offboardingFetchGen.current;
    if (!isOneill || !queryAgencies?.length) {
      setOpenOffboardings({ loading: false, agencies: {} });
      return;
    }
    lastOffboardingQueryRef.current = queryAgencies;
    setOpenOffboardings({ loading: true, agencies: {} });
    const results = await Promise.all(
      queryAgencies.map(fetchOneAgencyOffboardings)
    );
    if (offboardingFetchGen.current !== gen) return;
    setOpenOffboardings({
      loading: false,
      agencies: Object.assign({}, ...results)
    });
  }, []);

  const ensureAgenciesTree = async () => {
    if (agenciesTreeCacheRef.current) return agenciesTreeCacheRef.current;
    if (agenciesTree?.length) {
      agenciesTreeCacheRef.current = agenciesTree;
      return agenciesTree;
    }
    try {
      const res = await safeAWSCallGet(
        endpoints.aws.getAgencyTree,
        { agency: rootAgencyId },
        false
      );
      const tree = [res.data];
      agenciesTreeCacheRef.current = tree;
      setAgenciesTree(tree);
      return tree;
    } catch (e) {
      console.error('[offboarding] failed to load the agency tree:', e);
      return null;
    }
  };

  // For most users a single call with their own agency covers the whole
  // downline. O'Neill's id is special-cased by the Zoho function (its full
  // downline is the entire org, far too large to expand), so for O'Neill
  // users we query per selected agency instead, dropping agencies whose
  // selected ancestor already covers them.
  const resolveOffboardingQueryAgencies = async () => {
    if (!isOneillRootAgency) return [{ id: String(rootAgencyId) }];
    const selected = multiselectSelectedOptions.map(option => ({
      id: String(option.value),
      name: option.name ?? ''
    }));
    if (selected.length <= 1) return selected;
    const tree = await ensureAgenciesTree();
    if (!tree) return selected;
    const keptIds = new Set(
      consolidateAgenciesByHierarchy(
        tree,
        selected.map(a => a.id)
      )
    );
    return selected.filter(a => keptIds.has(a.id));
  };

  const initialOffboardingAgencyId = isOneillRootAgency
    ? String(id ?? '')
    : String(rootAgencyId ?? '');
  const lastInitialOffboardingAgencyIdRef = useRef(null);

  useEffect(() => {
    if (!initialOffboardingAgencyId) return;
    if (
      lastInitialOffboardingAgencyIdRef.current === initialOffboardingAgencyId
    )
      return;
    lastInitialOffboardingAgencyIdRef.current = initialOffboardingAgencyId;
    fetchOpenOffboardings([{ id: initialOffboardingAgencyId }]);
  }, [initialOffboardingAgencyId, fetchOpenOffboardings]);

  useEffect(() => {
    const aid = agencyData?.agencyInformation?.id?._value;
    if (!aid || String(aid) !== String(id)) return;
    bumpOnboardingLoad([
      {
        agencyId: aid,
        agencyName: agencyData.agencyInformation.name._value
      }
    ]);
  }, [agencyData?.agencyInformation?.id?._value, id, bumpOnboardingLoad]);

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();
    (async () => {
      if (id) {
        refreshUser(() => {}, cancelTokenSource);
        await getSpecificAgencyData(setLocalLoader, id);
      } else {
        navigate('/agencies');
      }
    })();

    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, [id]);

  const [timesInputClosed, setTimesInputClosed] = useState(0);

  //Action handlers
  const handleChangeSelectValue = selectedOptions => {
    setmultiselectSelectedOptions(selectedOptions);
  };

  const handleUpdateTable = async () => {
    if (localLoader || multiselectSelectedOptions.length === 0) return;
    apply();
    const selected_agencies_ids = multiselectSelectedOptions.map(
      option => option.value
    );
    await getSelectedAgenciesAgentsAndAppointmentCount(
      setLocalLoader,
      selected_agencies_ids
    );
    bumpOnboardingLoad(
      multiselectSelectedOptions.map(o => ({
        agencyId: o.value,
        agencyName: o.name
      }))
    );
    fetchOpenOffboardings(await resolveOffboardingQueryAgencies());
    setTimesInputClosed(timesInputClosed + 1);
  };

  const handleOffboardingSuccess = useCallback(async () => {
    await getSpecificAgencyData(setLocalLoader, id);
    fetchOpenOffboardings(lastOffboardingQueryRef.current);
  }, [id, getSpecificAgencyData, setLocalLoader, fetchOpenOffboardings]);

  const tabCounts = useMemo(() => {
    const counts = getAgentTabCounts(agencyDashboardAgents);
    counts.offboarding = countOpenOffboardingAgents(openOffboardings.agencies);
    return counts;
  }, [agencyDashboardAgents, openOffboardings.agencies]);

  const filteredAgents = useMemo(
    () => filterAgentsByTab(agencyDashboardAgents, agentTabId),
    [agencyDashboardAgents, agentTabId]
  );

  const handleExportAgents = useCallback(() => {
    const rows = agencyDashboardAgents.map(agent => ({
      agentName: `${agent.First_Name ?? ''} ${agent.Last_Name ?? ''}`.trim(),
      email: agent.Email ?? '',
      npn: agent.NPN ?? '',
      position: agent.Position_at_Agency || 'Agent',
      status: getAgentStatusLabel(
        agent,
        getOpenOffboardingRecord(openOffboardings.agencies, agent)
      ),
      agency: agent.Agency || '—',
      flags: getAgentFlags(agent)
    }));
    downloadCsv({
      filename: 'all_agents.csv',
      columns: AGENT_EXPORT_COLUMNS,
      rows
    });
  }, [agencyDashboardAgents, openOffboardings.agencies]);

  const displayAgents = useMemo(
    () =>
      filterAndSortAgents({
        agents: filteredAgents,
        searchQuery: agentSearchQuery,
        positionFilter,
        sortOption
      }),
    [filteredAgents, positionFilter, agentSearchQuery, sortOption]
  );

  const showAgentsSection =
    dashboardSectionTab === DASHBOARD_SECTION_IDS.AGENTS_TABLE;
  const isOffboardingTab = agentTabId === AGENT_TAB_IDS.OFFBOARDING;

  return (
    <div className='relative flex w-full flex-col gap-4 pb-2 min-w-0'>
      {!isMobileWebView() && (
        <Link className='mb-4' to='/agency/hierarchy'>
          <Button
            className='w-full sm:w-max'
            variant='secondary'
            size='xsmall'
            icon={<ChevronLeft />}
            text='Go to Agencies Hierarchy'
          />
        </Link>
      )}

      <div className='flex flex-col gap-4 shrink-0 rounded-xl border-b border-grey-lighter bg-white p-4 pb-0'>
        <div className='flex w-full shrink-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4'>
          <h2 className='m-0 text-2xl font-semibold! leading-none'>
            Agency Dashboard
          </h2>
        </div>
        <AgencyMultiSelectBar
          id='agencies'
          options={multiselectAvailableOptions}
          value={multiselectSelectedOptions}
          onChange={e => handleChangeSelectValue(e)}
          loading={localLoader}
          isDirty={isDirty}
          onUpdate={handleUpdateTable}
          updateDisabled={localLoader || multiselectSelectedOptions.length === 0}
          timesInputClosed={timesInputClosed}
          selectorDataTour='agency-selector'
          updateDataTour='agency-update-table'
        />
        <div data-tour='agency-section-tabs'>
          <AgencyDashboardSectionTabs
            tabs={DASHBOARD_SECTION_TABS}
            value={dashboardSectionTab}
            onChange={setDashboardSectionTab}
          />
        </div>
      </div>

      <div
        data-tour='agency-agents-table'
        className={cn(
          'w-full min-w-0 rounded-xl bg-white shadow-sm',
          localLoader && 'flex flex-col overflow-y-hidden',
          !localLoader && agencyDashboardAgents.length === 0 && 'p-4',
          !showAgentsSection && 'hidden'
        )}
        aria-hidden={!showAgentsSection}
      >
        {localLoader && (
          // keep the rank tabs visible (disabled, with spinners) and load only the table area
          <div className='flex flex-col gap-0 pt-4'>
            <h2 className='m-0 leading-none text-lg font-semibold! px-4 pb-2'>
              Agents
            </h2>
            <div className='shrink-0 px-4'>
              <AgentRankTabs
                activeTabId={agentTabId}
                onTabChange={setAgentTabId}
                counts={tabCounts}
                loading
              />
            </div>
            <div className='shrink-0 px-4'>
              <Separator />
            </div>
            <div className='flex flex-1 flex-col items-center justify-center px-4 py-12'>
              <LoadingContent message='Loading agents...' />
            </div>
          </div>
        )}
        {!localLoader && agencyDashboardAgents.length > 0 && (
          <div className='flex flex-col gap-0 pt-4'>
            <h2 className='m-0 leading-none text-lg font-semibold! px-4 pb-2'>
              Agents
            </h2>
            <div className='shrink-0 px-4'>
              <AgentRankTabs
                activeTabId={agentTabId}
                onTabChange={setAgentTabId}
                counts={tabCounts}
                offboardingLoading={openOffboardings.loading}
              />
            </div>
            <div className='shrink-0 px-4'>
              <Separator />
            </div>
            {isOffboardingTab ? (
              <div className='min-h-0 min-w-0 overflow-auto px-4 pb-4 pt-4'>
                <OpenOffboardingsTable
                  agencies={openOffboardings.agencies}
                  loading={openOffboardings.loading}
                  onOffboardingSuccess={handleOffboardingSuccess}
                />
              </div>
            ) : (
              <>
                <div className='shrink-0 px-4 pb-2 pt-4'>
                  <AgentsFiltersToolbar
                    agents={filteredAgents}
                    searchQuery={agentSearchQuery}
                    onSearchChange={setAgentSearchQuery}
                    positionFilter={positionFilter}
                    onPositionFilterChange={setPositionFilter}
                    sortOption={sortOption}
                    onSortChange={setSortOption}
                    onExportAgents={handleExportAgents}
                    exportDisabled={
                      agencyDashboardAgents.length === 0 ||
                      openOffboardings.loading
                    }
                  />
                </div>
                <div className='min-h-0 min-w-0 overflow-auto px-4 pb-4'>
                  <AgentsTable
                    agents={displayAgents}
                    openOffboardingAgencies={openOffboardings.agencies}
                    openOffboardingLoading={openOffboardings.loading}
                    onOffboardingSuccess={handleOffboardingSuccess}
                  />
                </div>
              </>
            )}
          </div>
        )}
        {!localLoader && agencyDashboardAgents.length === 0 && (
          <div className='flex flex-col items-center justify-center px-4 py-16'>
            <p className='text-xl font-semibold!'>No agents found</p>
            {!isMobileWebView() && (
              <Button
                className='w-full sm:w-max'
                onClick={() => navigate('/agency/hierarchy')}
                variant='secondary'
                size='small'
                icon={<ChevronLeft />}
                text='Go to Agencies'
              />
            )}
          </div>
        )}
      </div>

      <div
        className={cn(
          'w-full min-w-0 rounded-xl bg-white shadow-sm',
          localLoader && 'flex flex-col overflow-y-hidden',
          showAgentsSection && 'hidden'
        )}
        aria-hidden={showAgentsSection}
      >
        <div className='px-4 pt-4'>
          <h2 className='m-0 text-lg font-semibold! leading-none'>
            Onboarding Dashboard
          </h2>
        </div>
        <AgencyOnboardingDashboard onboardingLoad={onboardingLoad} />
      </div>
    </div>
  );
};

AgencyDashboard.propTypes = {
  agencyData: propTypes.object,
  agencyDashboardAgents: propTypes.arrayOf(object),
  agenciesTree: propTypes.array,
  setAgenciesTree: propTypes.func,
  refreshUser: propTypes.func,
  setLocalLoader: propTypes.func,
  localLoader: propTypes.bool,
  getSpecificAgencyData: propTypes.func,
  downloadAppointmentsReport: propTypes.func,
  getAgenciesTree: propTypes.func,
  getSelectedAgenciesAgentsAndAppointmentCount: propTypes.func
};

export default AgencyDashboard;
